/**
 * Quick Trade type definitions
 * Types for the press-and-hold quick trade functionality
 */

/**
 * Trade type
 */
export type TradeType = 'buy' | 'sell';

/**
 * Trade status values
 */
export type TradeStatus = 'queued' | 'processing' | 'running' | 'submission_unknown' | 'success' | 'failed' | 'error';

/**
 * Quick trade request payload
 */
export interface QuickTradeRequest {
  token_address: string;
  chain?: string;
  chain_id?: number;
  eth_amount?: string;
  sell_percent?: number;
  gas_limit?: string;
  max_priority_fee_per_gas?: string;
  bribe?: string;
  mev_protection?: boolean;
}

/**
 * Quick trade response
 */
export interface QuickTradeResponse {
  status: 'success' | 'error';
  trade_id?: number;
  message?: string;
}

export type BulkSellResultStatus = 'queued' | 'succeeded' | 'failed' | 'skipped' | 'unsupported';

export interface BulkSellRequest {
  chain_id: number;
  sell_percent: number;
  token_addresses: string[];
}

export interface BulkSellResult {
  token_address: string;
  status: BulkSellResultStatus;
  trade_id?: number | null;
  message?: string | null;
}

export interface BulkSellResponse {
  status: 'success' | 'partial_success' | 'error';
  sell_percent?: number;
  results: BulkSellResult[];
  queued_count?: number;
  skipped_count?: number;
  failed_count?: number;
  message?: string;
}

/**
 * Trade status update (from WebSocket)
 */
export interface TradeStatusUpdate {
  trade_id?: number;
  status: TradeStatus;
  display_status?: TradeStatus;
  raw_status?: string;
  submission_state?: string;
  lifecycle?: string;
  trade_type?: TradeType;
  token_address?: string;
  alpha_strategy_id?: number;
  chain_id?: number;
  avg_entry_price_usd?: number | null;
  message?: string;
  tx_hash?: string;
  mode?: string;
  included?: boolean;
  updated_at?: string;
  created_at?: string;
  wallet_address?: string;
}

/**
 * Strategy trade notification (from WebSocket)
 */
export interface StrategyTradeNotification {
  trade_id: number;
  trade_type: TradeType;
  token_address: string;
  chain_id: number;
  strategy_id?: number;
  strategy_name?: string;
  avg_entry_price_usd?: number | null;
  status: TradeStatus;
  message?: string;
  tx_hash?: string;
  wallet_address?: string;
}

/**
 * Status labels for display
 */
export const STATUS_LABELS: Record<TradeStatus, string> = {
  queued: 'Queued',
  processing: 'Processing',
  running: 'Processing',
  submission_unknown: 'Pending',
  success: 'Success',
  failed: 'Failed',
  error: 'Error',
};

/**
 * Status colors (Tailwind classes)
 */
export const STATUS_COLORS: Record<TradeStatus, string> = {
  queued: 'bg-gray-700 text-gray-200',
  processing: 'bg-blue-600 text-white',
  running: 'bg-blue-600 text-white',
  submission_unknown: 'bg-yellow-700 text-white',
  success: 'bg-green-600 text-white',
  failed: 'bg-red-600 text-white',
  error: 'bg-red-600 text-white',
};

/**
 * Terminal statuses (trade is complete)
 */
export const TERMINAL_STATUSES: TradeStatus[] = ['success', 'failed', 'error'];
